import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Chip } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NearMeIcon from '@mui/icons-material/NearMe';
import EventIcon from '@mui/icons-material/Event';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import BadgeIcon from '@mui/icons-material/Badge';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import GroupsIcon from '@mui/icons-material/Groups';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExploreIcon from '@mui/icons-material/Explore';
import StarIcon from '@mui/icons-material/Star';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface HeritageItem {
  id: string;
  name: string;
  distance: string;
  type: string;
  location: string;
  description?: string;
  color?: string;
  year?: string;
  imageUrl?: string;
  historicalImportance?: string;
  whyVisit?: string;
  bestTimeToVisit?: string;
}

interface HeritageContent {
  title: string;
  description: string;
  heritages: HeritageItem[];
}

const gradients: [string, string][] = [
  ['#f59e0b', '#d97706'],
  ['#8b5cf6', '#7c3aed'],
  ['#22c55e', '#16a34a'],
  ['#3b82f6', '#2563eb'],
  ['#ec4899', '#db2777'],
  ['#06b6d4', '#0891b2'],
];

const tips = [
  { text: 'Carry a valid photo ID for entry at historical sites', icon: BadgeIcon },
  { text: 'Photography may be restricted in certain areas', icon: NoPhotographyIcon },
  { text: 'Hire a local guide for detailed historical context', icon: RecordVoiceOverIcon },
  { text: 'Wear comfortable shoes for walking tours', icon: DirectionsWalkIcon },
  { text: 'Visit during weekdays to avoid weekend crowds', icon: GroupsIcon },
  { text: 'Check timings in advance — some sites close on Mondays', icon: ScheduleIcon },
];

export default function Heritage() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<HeritageContent>({
    title: 'Heritage Sites',
    description: 'Historical and heritage sites near Muchukunnu',
    heritages: []
  });

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'nearby_content', 'heritage'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as HeritageContent);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading heritage:', error);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  const items = content.heritages.map((h, i) => ({ ...h, gradient: gradients[i % gradients.length] }));

  return (
    <Box sx={{ bgcolor: '#fbf6ef', minHeight: '100vh' }}>
      <SEO title="Heritage Sites Near Kottayil Temple | പാരമ്പര്യ സ്ഥലങ്ങൾ" description="Heritage sites and historical landmarks near Kottayil Temple (Kotta-Kovilakam Kshethram) — museums, ancient sites, and cultural attractions in Kozhikode, Kerala." path="/nearby/heritage" keywords="heritage sites near temple, Kozhikode heritage, Kerala heritage, historical landmarks, കോഴിക്കോട് പാരമ്പര്യ സ്ഥലങ്ങൾ, museums near Muchukunnu" />
      <PageBanner title={content.title || 'Heritage Sites'} />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box
              sx={{
                borderRadius: '30px',
                overflow: 'hidden',
                mb: 4,
                boxShadow: '0 22px 64px rgba(100, 54, 18, 0.2)',
                background: 'linear-gradient(145deg, #6e3312 0%, #935019 48%, #c97c22 100%)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.13,
                  background:
                    'repeating-linear-gradient(120deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 2px, transparent 2px, transparent 14px)',
                }}
              />
              <Box sx={{ position: 'absolute', top: -56, right: -56, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -48, left: -48, width: 190, height: 190, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 72%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5, flexWrap: 'wrap' }}>
                  <Box sx={{
                    width: 66, height: 66, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.25)', boxShadow: '0 12px 30px rgba(58,28,10,0.28)',
                  }}>
                    <AccountBalanceIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      {content.title || 'Heritage Sites'}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.68)', fontWeight: 500 }}>
                      Walk through centuries of history
                    </Typography>
                  </Box>
                </Box>
                {content.description && (
                  <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.58)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                    {content.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.2 }}>
                  <Chip icon={<AccountBalanceIcon sx={{ fontSize: '16px !important', color: '#7f3a12 !important' }} />} label={`${items.length} Heritage Site${items.length !== 1 ? 's' : ''}`} sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#6f320f', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<HistoryEduIcon sx={{ fontSize: '16px !important', color: '#fde68a !important' }} />} label="Monuments & Stories" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.92)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.18)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' }, gap: 1.3 }}>
                  {[
                    { label: 'Heritage Directory', value: `${items.length}`, icon: AccountBalanceIcon },
                    { label: 'Cultural Focus', value: 'History & Legacy', icon: HistoryEduIcon },
                    { label: 'Visit Planning', value: 'Timings & Etiquette', icon: EventIcon },
                  ].map((metric) => {
                    const MIcon = metric.icon;
                    return (
                      <Box key={metric.label} sx={{ borderRadius: '14px', p: 1.4, bgcolor: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.16)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '9px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>{metric.value}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.7rem', lineHeight: 1.2 }}>{metric.label}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* CARDS */}
            {items.length === 0 ? (
              <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 6, textAlign: 'center', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <HistoryEduIcon sx={{ fontSize: 56, color: '#e0e0e0', mb: 2 }} />
                <Typography sx={{ fontSize: { xs: '0.88rem', md: '1rem' }, color: '#bbb', fontWeight: 600 }}>Heritage information coming soon.</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
                {items.map((h) => {
                  const [gStart, gEnd] = h.gradient;
                  return (
                    <Box key={h.id || h.name} sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(103,63,23,0.14)', boxShadow: '0 4px 24px rgba(95,61,28,0.1)',
                      transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 20px 60px rgba(95,61,28,0.2), 0 0 0 1px ${gStart}25` },
                    }}>
                      {/* Image or gradient banner */}
                      <Box sx={{ position: 'relative' }}>
                        {h.imageUrl ? (
                          <Box sx={{
                            width: '100%', height: { xs: 200, sm: 260 }, overflow: 'hidden',
                            position: 'relative',
                          }}>
                            <img
                              src={h.imageUrl} alt={h.name}
                              style={{
                                width: '100%', height: '100%', objectFit: 'cover',
                                transition: 'transform 600ms ease',
                              }}
                              onMouseOver={(e) => { (e.target as HTMLImageElement).style.transform = 'scale(1.05)'; }}
                              onMouseOut={(e) => { (e.target as HTMLImageElement).style.transform = 'scale(1)'; }}
                            />
                            {/* Gradient overlay on image */}
                            <Box sx={{
                              position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
                              background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
                            }} />
                            {/* Title overlay on image */}
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 2, sm: 2.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                  width: 42, height: 42, borderRadius: '14px',
                                  background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: `0 6px 20px ${gStart}50`,
                                }}>
                                  <AccountBalanceIcon sx={{ fontSize: 22, color: '#fff' }} />
                                </Box>
                                <Box>
                                  <Typography sx={{
                                    fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                    color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                    lineHeight: 1.2,
                                  }}>
                                    {h.name}
                                  </Typography>
                                  <Typography sx={{
                                    fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)',
                                    fontWeight: 500,
                                  }}>
                                    {h.location}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          /* No-image gradient header */
                          <Box sx={{
                            background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                            px: { xs: 2.5, sm: 3 }, py: { xs: 2, sm: 2.5 },
                            position: 'relative', overflow: 'hidden',
                          }}>
                            <Box sx={{ position: 'absolute', top: -15, right: -15, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                              <Box sx={{ width: 46, height: 46, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                                <AccountBalanceIcon sx={{ fontSize: 24, color: '#fff' }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.15rem' }, color: '#fff', lineHeight: 1.2 }}>
                                  {h.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                                  {h.location}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, bgcolor: '#fffaf3' }}>
                        {/* Tags row */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          {h.distance && (
                            <Chip icon={<NearMeIcon sx={{ fontSize: '15px !important', color: `${gStart} !important` }} />} label={h.distance} size="small" sx={{ bgcolor: `${gStart}10`, color: gStart, fontWeight: 700, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: `1px solid ${gStart}20`, '& .MuiChip-icon': { ml: '6px' } }} />
                          )}
                          {h.type && (
                            <Chip icon={<LocationOnIcon sx={{ fontSize: '14px !important', color: '#64748b !important' }} />} label={h.type} size="small" sx={{ bgcolor: 'rgba(100,116,139,0.08)', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: '1px solid rgba(100,116,139,0.12)', '& .MuiChip-icon': { ml: '6px' } }} />
                          )}
                          {h.location && !h.imageUrl && (
                            <Chip icon={<LocationOnIcon sx={{ fontSize: '14px !important', color: '#8b5cf6 !important' }} />} label={h.location} size="small" sx={{ bgcolor: 'rgba(139,92,246,0.08)', color: '#8b5cf6', fontWeight: 600, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: '1px solid rgba(139,92,246,0.12)', '& .MuiChip-icon': { ml: '6px' } }} />
                          )}
                          {h.year && (
                            <Chip icon={<EventIcon sx={{ fontSize: '14px !important', color: '#f59e0b !important' }} />} label={h.year} size="small" sx={{ bgcolor: 'rgba(245,158,11,0.08)', color: '#f59e0b', fontWeight: 700, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: '1px solid rgba(245,158,11,0.12)', '& .MuiChip-icon': { ml: '6px' } }} />
                          )}
                        </Box>

                        {/* Name (shown below tags when image is present, since the overlay already shows name) */}
                        {h.imageUrl && h.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 600 }}>
                              {h.location}
                            </Typography>
                          </Box>
                        )}

                        {/* Description */}
                        {h.description && (
                          <Typography sx={{
                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                            color: '#6f5b47',
                            lineHeight: 1.85,
                            letterSpacing: '0.01em',
                            mt: 0.5,
                          }}>
                            {h.description}
                          </Typography>
                        )}

                        {/* Historical Importance */}
                        {h.historicalImportance && h.historicalImportance.trim() && (
                          <Box sx={{ mt: 2.5, p: { xs: 2, sm: 2.5 }, bgcolor: '#fefce8', borderRadius: '16px', border: '1px solid #fef08a' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <Box sx={{ width: 30, height: 30, borderRadius: '10px', bgcolor: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <AccountBalanceIcon sx={{ fontSize: 16, color: '#fff' }} />
                              </Box>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#92400e' }}>
                                Historical Importance
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                              {h.historicalImportance.split('\n').filter(line => line.trim()).map((point, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b', mt: '7px', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: '0.84rem', color: '#78350f', lineHeight: 1.7, fontWeight: 500 }}>
                                    {point.trim()}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Why Visit */}
                        {h.whyVisit && h.whyVisit.trim() && (
                          <Box sx={{ mt: 2, p: { xs: 2, sm: 2.5 }, bgcolor: '#f0fdf4', borderRadius: '16px', border: '1px solid #bbf7d0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <Box sx={{ width: 30, height: 30, borderRadius: '10px', bgcolor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ExploreIcon sx={{ fontSize: 16, color: '#fff' }} />
                              </Box>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#166534' }}>
                                Why Visit
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                              {h.whyVisit.split('\n').filter(line => line.trim()).map((reason, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <CheckCircleOutlineIcon sx={{ fontSize: 17, color: '#22c55e', mt: '3px', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: '0.84rem', color: '#15803d', lineHeight: 1.7, fontWeight: 500 }}>
                                    {reason.trim()}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Best Time to Visit */}
                        {h.bestTimeToVisit && h.bestTimeToVisit.trim() && (
                          <Box sx={{ mt: 2, p: { xs: 2, sm: 2.5 }, bgcolor: '#eff6ff', borderRadius: '16px', border: '1px solid #bfdbfe' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <Box sx={{ width: 30, height: 30, borderRadius: '10px', bgcolor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <AccessTimeIcon sx={{ fontSize: 16, color: '#fff' }} />
                              </Box>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#1e40af' }}>
                                Best Time to Visit
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                              {h.bestTimeToVisit.split('\n').filter(line => line.trim()).map((time, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <StarIcon sx={{ fontSize: 15, color: '#3b82f6', mt: '4px', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: '0.84rem', color: '#1d4ed8', lineHeight: 1.7, fontWeight: 500 }}>
                                    {time.trim()}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* TIPS FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #332015 0%, #513122 45%, #26170f 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.06)', boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: 'linear-gradient(115deg, rgba(255,255,255,0.15) 0 2px, transparent 2px 12px)' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <TipsAndUpdatesIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>Visitor Tips</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Explore heritage responsibly</Typography>
                  </Box>
                </Box>
                <Grid container spacing={1.5}>
                  {tips.map((t, i) => {
                    const TIcon = t.icon;
                    return (
                      <Grid item xs={12} sm={6} key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5, borderRadius: '10px', transition: 'all 250ms', border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: '7px', bgcolor: `${GOLD}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <TIcon sx={{ fontSize: 13, color: GOLD }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.6 }}>{t.text}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="nearby" activePath="/nearby/heritage" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
